import { Component, OnDestroy, CUSTOM_ELEMENTS_SCHEMA, isDevMode } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import * as allIcons from 'ionicons/icons';
import { Capacitor } from '@capacitor/core';
import { StatusBar } from '@capacitor/status-bar';

// 👇 Router (para tracking de navegación)
import { Router, NavigationEnd } from '@angular/router';

// rxjs
import { Subscription, filter } from 'rxjs';

// ✅ Firebase Analytics (GA4)
import { getAnalytics, logEvent, setUserId } from 'firebase/analytics';

// ✅ Firestore (leer users/{uid} en boot para decisiones de landing y evitar flicker)
import { Firestore, doc, getDoc } from '@angular/fire/firestore';

// ⬇️ Entorno (para decisiones de PROD/DEV)
import { environment } from 'src/environments/environment';

// 👇 Servicios propios
import { AuthService } from './services/auth.service';
import { TickerService } from './services/ticker.service';
import { ChallengeService } from './services/challenge.service';
import { WebPushService } from './web-push.service';
import { NativePushService } from './services/native-push.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppComponent implements OnDestroy {
  private authSub?: Subscription;
  private routeSub?: Subscription;

  private lastUid: string | null = null;

  private webPushInitialized = false; // evita inicializar WebPush dos veces
  private nativePushInitialized = false; // evita inicializar NativePush dos veces

  // ✅ Boot overlay control (anti-flicker)
  private bootResolved = false;
  private bootMinMs = 450; // mínimo para evitar “flash” visual raro
  private bootTimeoutMs = 3500; // timeout defensivo si Firestore tarda/offline

  // ✅ Evitar que el arranque te “enganche” en /explore/:id cuando ya acabaste la intro
  private initialLandingHandled = false;

  // ✅ PWA reload: si recargas estando en /tabs/explore/:id, vuelve a /tabs/explore
  private forceBaseOnPwaReload = false;

  // 🔔 Re-init throttle (evita spam si se dispara varias veces)
  private lastPushReinitAt = 0;

  constructor(
    private authService: AuthService,
    private ticker: TickerService,
    private challenges: ChallengeService,
    private webPush: WebPushService,
    private nativePush: NativePushService,
    private router: Router,
    private firestore: Firestore
  ) {
    // Registrar todos los iconos Ionicons
    addIcons(allIcons);

    // ✅ scroll = none (evita restauración automática rara en PWA)
    this.setupScrollRestoration();

    // ✅ Mantener el boot overlay hasta que resolvamos estado (quitamos auto-hide CSS si existe)
    this.holdBootSplash();

    // ✅ Si estamos en PWA y esto es un RELOAD, no queremos quedar “enganchados” al detalle
    this.forceBaseOnPwaReload = this.shouldForceBaseOnPwaReload();

    const platform = Capacitor.getPlatform();
    console.log('[App] Platform on boot:', platform);
    if (platform !== 'web') {
      try {
        StatusBar.setOverlaysWebView({ overlay: false });
      } catch (e) {
        console.warn('[App] StatusBar overlay set failed:', e);
      }
    }

    // ✅ Analytics: track de rutas (SPA) -> page_view
    this.initRouteAnalytics();

    // 🔹 Manejo del login con redirect (si aplica)
    this.authService.handleRedirectResult().then((user) => {
      if (user) {
        console.log('✅ Usuario autenticado via redirect:', user.email);
        this.setAnalyticsUser(user.uid);
      } else {
        console.log('ℹ️ No hay sesión activa (redirect)');
      }
    });

    // 🔁 Reaccionar a cambios de sesión para arrancar/parar el Ticker y Push
    this.authSub = this.authService.onAuthStateChanged$().subscribe(async (user) => {
      const newUid = user?.uid ?? null;

      // ✅ Analytics: user id (solo si cambia)
      if (newUid !== this.lastUid) {
        this.setAnalyticsUser(newUid);
      }

      this.lastUid = newUid;

      console.log('[App] onAuthStateChanged$ user:', this.lastUid);

      // ✅ Resolver boot (anti-flicker) solo una vez en el arranque
      if (!this.bootResolved) {
        await this.resolveBootAndHideSplash(this.lastUid);
      }

      if (user?.uid) {
        try {
          await this.challenges.syncDayFromAnchor(user.uid);
        } catch (e) {
          console.warn('syncDayFromAnchor (login) warning:', e);
        }

        this.ticker.start();

        // 🔔 Inicializar push (web o nativo, según plataforma)
        this.initWebPushOnce();
        this.initNativePushOnce();
      } else {
        this.ticker.stop();

        // ✅ Si haces logout, permitimos que al volver a loguear se reinicialice
        this.webPushInitialized = false;
        this.nativePushInitialized = false;

        // ✅ IMPORTANTÍSIMO: también reseteamos el flag interno del Service (best-effort)
        try { (this.webPush as any).initialized = false; } catch {}
        try { (this.nativePush as any).initialized = false; } catch {}
      }
    });

    // 🟣 Arranque defensivo: si ya hay usuario al cargar (antes del subscribe)
    const bootUser = this.authService.getCurrentUser();
    if (bootUser?.uid) {
      this.lastUid = bootUser.uid;
      console.log('[App] bootUser ya logueado:', this.lastUid);

      this.setAnalyticsUser(this.lastUid);

      (async () => {
        if (!this.bootResolved) {
          await this.resolveBootAndHideSplash(this.lastUid);
        }

        try {
          await this.challenges.syncDayFromAnchor(bootUser.uid);
        } catch (e) {
          console.warn('syncDayFromAnchor (boot) warning:', e);
        }

        this.ticker.start();

        // 🔔 Inicializar push en arranque si ya hay usuario
        this.initWebPushOnce();
        this.initNativePushOnce();
      })();
    } else {
      (async () => {
        if (!this.bootResolved) {
          await this.resolveBootAndHideSplash(null);
        }
      })();
    }

    // 🔔 Re-sync rápido al volver al foreground / pestaña visible
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('focus', this.onFocus);

    // ✅ NUEVO: reinit push al recibir señal (por ejemplo tras HARD RESET)
    window.addEventListener('challup:push-reinit', this.onPushReinit);

    // =======================
    // 🧪 Ganchos de DEBUG
    // =======================

    (window as any).__challupDebugWriteDevCurrent = async (day = 999) => {
      try {
        const uid = this.lastUid;
        if (!uid) {
          console.warn('No hay usuario logueado');
          return;
        }
        if (typeof (this.challenges as any).__debugForceDevCurrent !== 'function') {
          console.warn('__debugForceDevCurrent no existe en ChallengeService');
          return;
        }
        await (this.challenges as any).__debugForceDevCurrent(uid, day);
        console.log('✅ devCurrent creado/actualizado. Revisa Firestore → users/{uid}/devCurrent');
      } catch (e) {
        console.error(e);
      }
    };

    (window as any).__challupInitPush = async () => {
      try {
        console.log('[App] __challupInitPush llamado');
        await this.onPushReinit();
        console.log('✅ WebPush/NatPush reinit lanzado manualmente');
      } catch (e) {
        console.error('Push reinit manual error', e);
      }
    };

    (window as any).__challupInitNativePush = async () => {
      try {
        console.log('[App] __challupInitNativePush llamado');
        await this.onPushReinit();
        console.log('✅ NativePush reinit lanzado manualmente');
      } catch (e) {
        console.error('NativePush reinit manual error', e);
      }
    };
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.routeSub?.unsubscribe();
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('challup:push-reinit', this.onPushReinit);
    this.ticker.stop();
  }

  // =======================
  // ✅ PWA / Scroll helpers
  // =======================

  private setupScrollRestoration() {
    try {
      if (typeof window === 'undefined') return;
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
    } catch {
      // ignore
    }
  }

  private isStandalonePwa(): boolean {
    try {
      if (typeof window === 'undefined') return false;
      const mm = window.matchMedia?.('(display-mode: standalone)');
      const standaloneMatch = !!mm?.matches;
      const iOSStandalone = (window.navigator as any)?.standalone === true;
      return standaloneMatch || iOSStandalone;
    } catch {
      return false;
    }
  }

  private getNavigationType(): string | null {
    try {
      if (typeof window === 'undefined') return null;

      const navEntries = performance.getEntriesByType?.('navigation') as any;
      if (navEntries && navEntries.length > 0) {
        return navEntries[0]?.type ?? null;
      }

      const legacy = (performance as any).navigation;
      if (legacy && typeof legacy.type === 'number') {
        if (legacy.type === 1) return 'reload';
        if (legacy.type === 2) return 'back_forward';
        if (legacy.type === 0) return 'navigate';
      }
    } catch {}

    return null;
  }

  private shouldForceBaseOnPwaReload(): boolean {
    try {
      if (typeof window === 'undefined') return false;
      if (!this.isStandalonePwa()) return false;

      const navType = this.getNavigationType();
      if (navType !== 'reload') return false;

      const path = (window.location.pathname || '/').split('?')[0] || '/';
      const isExploreDetail = /^\/tabs\/explore\/[^/]+/.test(path) || /^\/explore\/[^/]+/.test(path);

      return isExploreDetail;
    } catch {
      return false;
    }
  }

  // =======================
  // ✅ Boot overlay helpers (anti-flicker)
  // =======================

  private holdBootSplash() {
    try {
      if (typeof document === 'undefined') return;
      const el = document.querySelector('.boot-splash') as HTMLElement | null;
      if (!el) return;

      el.style.animation = 'none';
      el.style.opacity = '1';
      el.style.visibility = 'visible';
      el.style.pointerEvents = 'all';
    } catch {
      // ignore
    }
  }

  private async resolveBootAndHideSplash(uid: string | null) {
    if (this.bootResolved) return;
    this.bootResolved = true;

    const startedAt = Date.now();

    if (uid) {
      try {
        await this.withTimeout(
          this.bootTimeoutMs,
          (async () => {
            const ref = doc(this.firestore, `users/${uid}`);
            const snap = await getDoc(ref);

            if (snap.exists()) {
              const data = snap.data() as any;
              console.log('[Boot] users/{uid} loaded');
              this.handleInitialLandingIfNeeded(data);
            } else {
              console.log('[Boot] users/{uid} missing (still ok)');
              this.handleInitialLandingIfNeeded(null);
            }
          })()
        );
      } catch (e) {
        console.warn('[Boot] profile load timeout/warn:', e);
      }
    }

    const elapsed = Date.now() - startedAt;
    const waitMore = Math.max(0, this.bootMinMs - elapsed);
    if (waitMore > 0) {
      await new Promise((r) => setTimeout(r, waitMore));
    }

    this.hideBootSplash();
  }

  private hideBootSplash() {
    try {
      if (typeof document === 'undefined') return;
      const el = document.querySelector('.boot-splash') as HTMLElement | null;
      if (!el) return;

      el.style.animation = 'challupBootFadeOut 0.18s ease 0s forwards';
      el.style.pointerEvents = 'none';
    } catch {
      // ignore
    }
  }

  private async withTimeout<T>(ms: number, p: Promise<T>): Promise<T> {
    return await Promise.race([
      p,
      new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`timeout_${ms}ms`)), ms)),
    ]);
  }

  // =======================
  // ✅ Landing control
  // =======================

  private isIntroFinishedFromUserDoc(data: any): boolean {
    if (!data) return false;

    if (data.introFinished === true) return true;
    if (data.introCompleted === true) return true;
    if (data.introDone === true) return true;
    if (data.sequenceCompleted === true) return true;
    if (data.basePathUnlocked === true) return true;
    if (data.pathBaseUnlocked === true) return true;
    if (data.caminoBaseUnlocked === true) return true;
    if (data.finished20 === true) return true;

    const idx =
      (typeof data.currentIntroIndex === 'number' ? data.currentIntroIndex : null) ??
      (typeof data.introIndex === 'number' ? data.introIndex : null) ??
      (typeof data.currentChallengeIndex === 'number' ? data.currentChallengeIndex : null) ??
      (typeof data.introCount === 'number' ? data.introCount : null) ??
      (typeof data.completedIntroCount === 'number' ? data.completedIntroCount : null);

    if (typeof idx === 'number' && idx >= 20) return true;

    const completed =
      (typeof data.completedIntro === 'number' ? data.completedIntro : null) ??
      (typeof data.introCompletedCount === 'number' ? data.introCompletedCount : null);

    if (typeof completed === 'number' && completed >= 20) return true;

    const progress = data.progress;
    if (progress) {
      if (progress.introFinished === true) return true;
      if (typeof progress.completedIntroCount === 'number' && progress.completedIntroCount >= 20)
        return true;
      if (typeof progress.currentIntroIndex === 'number' && progress.currentIntroIndex >= 20)
        return true;
    }

    return false;
  }

  private handleInitialLandingIfNeeded(userDocData: any) {
    if (this.initialLandingHandled) return;
    this.initialLandingHandled = true;

    const url = (this.router.url || '/').split('?')[0] || '/';
    const isExploreDetail = /^\/tabs\/explore\/[^/]+/.test(url) || /^\/explore\/[^/]+/.test(url);
    if (!isExploreDetail) return;

    const introFinished = this.isIntroFinishedFromUserDoc(userDocData);

    if (introFinished) {
      console.log('[Boot] Intro finished + explore detail detected -> redirect to /tabs/explore');
      try {
        this.router.navigateByUrl('/tabs/explore', { replaceUrl: true });
      } catch (e) {
        console.warn('[Boot] Redirect to /tabs/explore failed:', e);
      }
    } else {
      console.log('[Boot] Explore detail on boot, but intro NOT finished -> keep current route');
    }
  }

  // =======================
  // ✅ Analytics helpers
  // =======================

  private getAnalyticsSafe() {
    if (typeof window === 'undefined') return null;
    try {
      return getAnalytics();
    } catch {
      return null;
    }
  }

  private logAnalytics(eventName: string, params: Record<string, any> = {}) {
    const a = this.getAnalyticsSafe();
    if (!a) return;

    const payload: Record<string, any> = { ...params };
    if (isDevMode()) payload['debug_mode'] = true;

    try {
      logEvent(a, eventName, payload);
    } catch {
      // ignore
    }
  }

  private setAnalyticsUser(uid: string | null) {
    const a = this.getAnalyticsSafe();
    if (!a) return;

    try {
      setUserId(a, (uid ?? null) as any);
    } catch {
      // ignore
    }
  }

  private initRouteAnalytics() {
    this.routeSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        try {
          const path = (e.urlAfterRedirects || e.url || '').split('?')[0] || '/';

          if (this.forceBaseOnPwaReload) {
            this.forceBaseOnPwaReload = false;

            const isExploreDetail =
              /^\/tabs\/explore\/[^/]+/.test(path) || /^\/explore\/[^/]+/.test(path);

            if (isExploreDetail) {
              try { window.scrollTo(0, 0); } catch {}
              try {
                this.router.navigateByUrl('/tabs/explore', { replaceUrl: true });
              } catch (err) {
                console.warn('[PWA Reload] redirect to /tabs/explore failed:', err);
              }
              return;
            }
          }

          const location = typeof window !== 'undefined' ? window.location.href : undefined;

          this.logAnalytics('page_view', {
            page_path: path,
            page_title: path,
            page_location: location,
          });
        } catch {
          // ignore
        }
      });
  }

  // =======================
  // 🔔 Push init/reinit
  // =======================

  private async initWebPushOnce(force = false) {
    if (this.webPushInitialized && !force) {
      console.log('[App] WebPush ya inicializado, skip');
      return;
    }

    const platform = Capacitor.getPlatform();
    console.log('[App] initWebPushOnce platform:', platform);

    if (platform !== 'web') {
      console.log('[App] No es web, no inicializo WebPush');
      return;
    }

    try {
      if (typeof window === 'undefined') return;
      if (!('Notification' in window)) {
        console.log('[App] Notification API no disponible, skip WebPush');
        return;
      }
      if (!('serviceWorker' in navigator)) {
        console.log('[App] serviceWorker no disponible, skip WebPush');
        return;
      }
    } catch {
      return;
    }

    // ✅ En PROD: solo auto-inicializamos WebPush si es PWA instalada (standalone),
    // pero si force=true (reinit), permitimos intentar.
    if (environment.production && !force && !this.isStandalonePwa()) {
      console.log('[App] PROD + no-standalone -> no auto-init WebPush (evita prompt en web normal)');
      return;
    }

    this.webPushInitialized = true;

    try {
      console.log('[App] Llamando a WebPushService.init()');
      await this.webPush.init();
      console.log('[App] WebPushService.init() OK');
    } catch (err) {
      console.error('Error inicializando WebPushService:', err);
      this.webPushInitialized = false;
    }
  }

  private async initNativePushOnce(force = false) {
    if (this.nativePushInitialized && !force) {
      console.log('[App] NativePush ya inicializado, skip');
      return;
    }

    const platform = Capacitor.getPlatform();
    console.log('[App] initNativePushOnce platform:', platform);

    if (platform === 'web') {
      console.log('[App] Es web, no inicializo NativePush');
      return;
    }

    this.nativePushInitialized = true;

    try {
      console.log('[App] Llamando a NativePushService.init()');
      await this.nativePush.init();
      console.log('[App] NativePushService.init() OK');
    } catch (err) {
      console.error('Error inicializando NativePushService:', err);
      this.nativePushInitialized = false;
    }
  }

  private onPushReinit = async () => {
    try {
      const now = Date.now();
      if (now - this.lastPushReinitAt < 1500) {
        console.log('[App] push-reinit throttled');
        return;
      }
      this.lastPushReinitAt = now;

      console.log('[App] challup:push-reinit recibido');

      if (!this.lastUid) {
        console.log('[App] push-reinit: no hay uid, skip');
        return;
      }

      // ✅ Reseteo flags del AppComponent
      this.webPushInitialized = false;
      this.nativePushInitialized = false;

      // ✅ CLAVE: reseteo best-effort del flag interno de los services
      // (si no, WebPushService.init() puede “salir” sin hacer nada)
      try { (this.webPush as any).initialized = false; } catch {}
      try { (this.nativePush as any).initialized = false; } catch {}

      const platform = Capacitor.getPlatform();
      if (platform === 'web') {
        await this.initWebPushOnce(true);
      } else {
        await this.initNativePushOnce(true);
      }

      this.logAnalytics('push_reinit', { platform: String(platform || '') });
    } catch (e) {
      console.warn('[App] push-reinit warning:', e);
    }
  };

  // ===== Handlers =====

  private onVisibility = async () => {
    if (document.visibilityState === 'visible' && this.lastUid) {
      try {
        await this.challenges.syncDayFromAnchor(this.lastUid);
      } catch (e) {
        console.warn('syncDayFromAnchor (visibility) warning:', e);
      }
    }
  };

  private onFocus = async () => {
    if (this.lastUid) {
      try {
        await this.challenges.syncDayFromAnchor(this.lastUid);
      } catch (e) {
        console.warn('syncDayFromAnchor (focus) warning:', e);
      }
    }
  };
}
